import { useEffect, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { listMySessions, type SessionSummary } from '@/api/client'
import { AppShell } from '@/components/layout/AppShell'
import { EmptyState } from '@/components/spectrumui/empty-state'
import { Skeleton } from '@/components/ui/skeleton'
import { recBadge, statusBadge, formatDate } from '@/lib/dashboard-utils'
import { usePageMeta } from '@/hooks/use-page-meta'

type Filter = 'all' | 'completed' | 'open'

export function MyInterviews() {
  usePageMeta("My interviews — Aura")
  const navigate = useNavigate()
  const [sessions, setSessions] = useState<SessionSummary[] | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [search, setSearch] = useState('')
  const [filter, setFilter] = useState<Filter>('all')

  useEffect(() => {
    let cancelled = false
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setSessions(null)
    setError(null)
    listMySessions()
      .then(data => { if (!cancelled) setSessions(data) })
      .catch(err => {
        if (cancelled) return
        console.error('Failed to load user sessions:', {
          status: err?.response?.status,
          message: err?.message
        })
        if (err?.response?.status === 401 || err?.response?.status === 403) {
          navigate('/', { replace: true })
          return
        }
        setError('Failed to load your interviews. Please try again.')
      })
    return () => { cancelled = true }
  }, [navigate])

  // Client-side filters only: the API has no query params on /sessions/mine,
  // and the lists are personal-scale.
  const filtered = (sessions ?? [])
    .filter(s => filter === 'all'
      || (filter === 'completed' ? s.status === 'completed' : s.status !== 'completed'))
    .filter(s => s.candidate_name.toLowerCase().includes(search.toLowerCase()))

  const chips: { key: Filter; label: string }[] = [
    { key: 'all', label: 'All' },
    { key: 'completed', label: 'Scored' },
    // "Open", not "In progress": the filter matches status !== 'completed',
    // so pending (never-finished) sessions land here too.
    { key: 'open', label: 'Open' },
  ]

  return (
    <AppShell active="my-interviews">
      {error && <div className="wbanner" role="alert">{error}</div>}

      {sessions === null && !error ? (
        <div role="status" aria-live="polite" className="flex flex-col gap-3">
          <Skeleton className="h-9 w-64" />
          <Skeleton className="h-11 w-full" />
          {Array.from({ length: 6 }).map((_, i) => (
            <Skeleton key={i} className="h-14 w-full" />
          ))}
        </div>
      ) : sessions && sessions.length === 0 ? (
        <section className="panel">
          <EmptyState
            eyebrow="Start here"
            title="No interviews yet"
            description="Once you complete an interview, it'll show up here."
            action={<Link to="/interview" className="wbtn wbtn-primary">Start your first interview</Link>}
          />
        </section>
      ) : (
        <>
          <header className="screen-head">
            <div>
              <h1>Your sessions</h1>
              <p>Review reports, and keep your next retry close at hand.</p>
            </div>
            <div className="screen-actions">
              <Link to="/interview" className="wbtn wbtn-primary">
                New interview
                <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M5 12h14M13 6l6 6-6 6"/></svg>
              </Link>
            </div>
          </header>

          <div className="wfilters" role="group" aria-label="Filter interviews">
            {chips.map(c => (
              <button
                key={c.key}
                type="button"
                className="chip"
                aria-pressed={filter === c.key}
                onClick={() => setFilter(c.key)}
              >
                {c.label}
              </button>
            ))}
            <input
              type="search"
              className="wsearch"
              placeholder="Search by candidate name…"
              value={search}
              onChange={e => setSearch(e.target.value)}
              aria-label="Search interviews"
            />
          </div>

          {/* Keyed by filter (not search): switching chips remounts the
              list so rows cascade; typing keeps the list calm. */}
          <section className="panel" aria-label="Interview list">
            <div className="panel-head">
              <h2>All sessions</h2>
              <span className="panel-meta">{filtered.length} shown</span>
            </div>
            {filtered.length === 0 ? (
              <EmptyState
                eyebrow="No matches"
                title="Nothing matches"
                description="No interviews in this filter yet — clear the search or switch filter."
              />
            ) : (
              <ul className="list wlist-stagger" key={filter}>
                {filtered.map(s => {
                  const canView = s.status === 'completed'
                  const badge = statusBadge(s.status)
                  return (
                    <li className="list-item" key={s.session_id}>
                      {canView ? (
                        <button type="button" className="row" onClick={() => navigate(`/my-interviews/${s.session_id}`)}>
                          <span>
                            <span className="row-title">{formatDate(s.created_at)}</span>
                            <span className="row-meta">
                              {s.candidate_name}
                              {s.overall_score !== null && s.recommendation ? ` · ${recBadge(s.recommendation).text}` : ''}
                            </span>
                          </span>
                          <span className={s.overall_score !== null ? 'row-score' : 'row-score none'}>
                            {s.overall_score ?? '—'}
                          </span>
                          <span className={badge.cls}>{badge.text}</span>
                        </button>
                      ) : (
                        <div className="row">
                          <span>
                            <span className="row-title">{formatDate(s.created_at)}</span>
                            <span className="row-meta">{s.candidate_name}</span>
                          </span>
                          <span className="row-score none">—</span>
                          <span className={badge.cls}>{badge.text}</span>
                        </div>
                      )}
                    </li>
                  )
                })}
              </ul>
            )}
          </section>
        </>
      )}
    </AppShell>
  )
}
